import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Typography, Box, TextField, IconButton, ClickAwayListener } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { FiTrendingUp } from 'react-icons/fi';
import { IoWarningOutline } from 'react-icons/io5';
import { MdOutlineEdit, MdCheck } from 'react-icons/md';
import { COLORS } from '../../constants';
import { useCardStyles } from '../../styles';
import MetricCard from './MetricCard';

// Estilos específicos de ImplementationCard
const useLocalStyles = makeStyles(() => ({
    percentageUp: {
        display: 'flex',
        alignItems: 'center',
        color: COLORS.success,
        fontWeight: 'bold',
        fontSize: '16px',
    },
    editContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
    },
    totalInput: {
        width: '80px',
        '& .MuiInputBase-input': {
            padding: '2px 6px',
            fontSize: '14px',
            textAlign: 'center',
        },
        '& .MuiOutlinedInput-root': {
            height: '28px',
        },
    },
    editIcon: {
        cursor: 'pointer',
        color: '#9e9e9e',
        verticalAlign: 'middle',
        marginLeft: '2px',
        '&:hover': {
            color: COLORS.primary,
        },
    },
    warningIcon: {
        color: COLORS.error,
        fontSize: '20px',
    },
    warningValue: {
        color: COLORS.error,
        fontWeight: 'bold',
        fontSize: '16px',
    },
}));

/**
 * Tarjeta de métricas de implementación
 * Muestra usuarios medidos, porcentaje de implementación y usuarios sin mediciones
 * @param {Object} props - Props del componente
 * @param {Object} props.data - Datos de implementación
 * @param {number} props.data.measuredUsers - Usuarios medidos
 * @param {number} props.data.totalUsers - Total de usuarios
 * @param {number} props.data.implementationPercentage - Porcentaje de implementación
 * @param {number} props.data.usersWithoutMeasurements - Usuarios sin mediciones
 * @param {string} props.month - Mes actual
 * @param {number} props.year - Año actual
 * @param {function} props.onTotalUsersChange - Callback cuando el usuario edita el total
 * @returns {JSX.Element} Tarjeta de implementación
 */
export default function ImplementationCard({ 
    data = {},
    month = '',
    year = '',
    onTotalUsersChange,
}) {
    const cardClasses = useCardStyles();
    const localClasses = useLocalStyles();

    const {
        measuredUsers = 0,
        totalUsers = 0,
        implementationPercentage = 0,
        usersWithoutMeasurements = 0,
    } = data;

    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(totalUsers);

    const periodLabel = month && year ? `${month} ${year}` : 'Sin período';

    const handleEditConfirm = () => {
        const parsed = parseInt(editValue, 10);
        if (!isNaN(parsed) && parsed > 0 && onTotalUsersChange) {
            onTotalUsersChange(parsed);
        }
        setIsEditing(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleEditConfirm();
        if (e.key === 'Escape') {
            setEditValue(totalUsers);
            setIsEditing(false);
        }
    };

    // Contenido de la métrica principal
    const mainMetric = (
        <>
            <Typography className={cardClasses.metricValue}>
                {measuredUsers} Usuarios medidos
            </Typography>
            {isEditing ? (
                <ClickAwayListener onClickAway={handleEditConfirm}>
                    <Box className={localClasses.editContainer}>
                        <Typography className={cardClasses.metricLabel}>de</Typography>
                        <TextField
                            className={localClasses.totalInput}
                            variant="outlined"
                            size="small"
                            type="number"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            autoFocus
                            inputProps={{ min: 1 }}
                        />
                        <Typography className={cardClasses.metricLabel}>Totales</Typography>
                        <IconButton size="small" onClick={handleEditConfirm}>
                            <MdCheck size={20} />
                        </IconButton>
                    </Box>
                </ClickAwayListener>
            ) : (
                <Typography
                    className={cardClasses.metricLabel}
                    style={{ cursor: 'pointer' }}
                    onClick={() => { setEditValue(totalUsers); setIsEditing(true); }}
                >
                    de {totalUsers} Totales <MdOutlineEdit size={22} className={localClasses.editIcon} />
                </Typography>
            )}
        </>
    );

    // Contenido de la fila de estadísticas
    const statsRow = (
        <>
            <Box className={localClasses.percentageUp}>
                <FiTrendingUp className={cardClasses.percentageIcon} />
                {implementationPercentage}%
            </Box>
            <Typography className={cardClasses.metricLabel}>
                Porcentaje de implementación
            </Typography>
        </>
    );

    // Contenido de la fila de advertencia
    const warningRow = (
        <>
            <IoWarningOutline className={localClasses.warningIcon} />
            <Typography className={localClasses.warningValue}>
                {usersWithoutMeasurements}
            </Typography>
            <Typography className={cardClasses.warningLabel}>
                Usuarios sin mediciones
            </Typography>
        </>
    );

    return (
        <MetricCard
            title="Implementación"
            subtitle={periodLabel}
            mainMetric={mainMetric}
            statsRow={statsRow}
            warningRow={warningRow}
        />
    );
}

ImplementationCard.propTypes = {
    data: PropTypes.shape({
        measuredUsers: PropTypes.number.isRequired,
        totalUsers: PropTypes.number.isRequired,
        implementationPercentage: PropTypes.number.isRequired,
        usersWithoutMeasurements: PropTypes.number.isRequired,
    }).isRequired,
    month: PropTypes.string.isRequired,
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onTotalUsersChange: PropTypes.func,
};
